import React, { useState } from "react";
import api, { API } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { COMPANY_LOGO, COMPANY_NAME, COMPANY_FULL, PRODUCT_NAME } from "../lib/brand";

const DEMO_USERS = [
  { email: "shipper@speedyship.demo", name: "Sara Shipper", role: "shipper" },
  { email: "carrier@speedyship.demo", name: "Carl Carrier", role: "carrier_owner" },
  { email: "dispatcher@speedyship.demo", name: "Dana Dispatcher", role: "dispatcher" },
  { email: "driver@speedyship.demo", name: "Dave Driver", role: "driver" },
  { email: "admin@speedyship.demo", name: "Avery Admin", role: "super_admin" },
];

export default function Login() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);

  const googleLogin = () => {
    window.location.href = `${API}/auth/google/start?next=${encodeURIComponent("/app")}`;
  };

  const demoLogin = async (u) => {
    setBusy(true);
    try {
      await api.post("/auth/dev-login", u);
      await api.post("/seed").catch(() => {});
      await refresh();
      navigate("/app");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      <div className="md:w-1/2 bg-navy text-white p-10 md:p-16 flex flex-col justify-between relative overflow-hidden">
        <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-gold/10 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-md bg-white flex items-center justify-center overflow-hidden shadow-lg flex-shrink-0">
              <img src={COMPANY_LOGO} alt={COMPANY_FULL} className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-display text-xl font-bold leading-tight">{COMPANY_NAME}</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-gold font-bold mt-0.5">on {PRODUCT_NAME}</div>
            </div>
          </div>
        </div>
        <div className="relative">
          <div className="text-[11px] tracking-[0.2em] uppercase text-gold font-bold mb-4">Welcome back</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter leading-tight mb-4">
            Sign in to <br /> the load board.
          </h1>
          <p className="text-slate-300 max-w-md">
            Use Google for production access, or pick a demo account to instantly explore every role.
          </p>
        </div>
        <div className="relative text-xs text-slate-400">© 2026 {COMPANY_FULL}</div>
      </div>

      <div className="md:w-1/2 p-10 md:p-16 flex items-center bg-slate-50">
        <div className="w-full max-w-md mx-auto">
          <h2 className="font-display text-3xl font-bold text-navy tracking-tight mb-2">Sign in</h2>
          <p className="text-slate-500 mb-8">Continue to your dashboard</p>

          <button onClick={googleLogin} className="btn-secondary w-full py-3 text-base" data-testid="google-signin-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M22 12.2c0-.8-.1-1.4-.2-2H12v3.8h5.7c-.2 1.3-1 3.3-2.9 4.3l-.026.176 4.21 3.255.293.03c2.7-2.5 4.2-6.2 4.2-9.6Z" fill="#4285F4"/>
              <path d="M12 22c3.8 0 7-1.3 9.3-3.4l-4.5-3.5c-1.2.8-2.8 1.4-4.8 1.4-3.7 0-6.8-2.4-7.9-5.8L4 10.8l-.2.1L1.4 13.6l-.1.2C3.6 18.7 7.5 22 12 22Z" fill="#34A853"/>
              <path d="M4.1 10.7c-.3-.8-.4-1.7-.4-2.7 0-.9.2-1.8.4-2.7L4 5.2 1.4 3l-.1.1C.5 4.7 0 6.7 0 8.5c0 1.8.5 3.8 1.3 5.4l2.8-2.2Z" fill="#FBBC05"/>
              <path d="M12 3.5c2.6 0 4.4 1.1 5.4 2.1l4-3.9C18.9.9 15.8 0 12 0 7.5 0 3.6 3.3 1.3 8l2.8 2.2C5.2 5.9 8.3 3.5 12 3.5Z" fill="#EB4335"/>
            </svg>
            Continue with Google
          </button>

          <div className="my-8 flex items-center gap-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-400">
            <div className="flex-1 h-px bg-slate-200" />
            Or try demo
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <div className="space-y-2">
            {DEMO_USERS.map(u => (
              <button
                key={u.email}
                disabled={busy}
                onClick={() => demoLogin(u)}
                className="w-full text-left card-base p-3 hover:border-gold transition-colors flex items-center gap-3 disabled:opacity-50"
                data-testid={`demo-login-${u.role}`}
              >
                <div className="w-10 h-10 rounded-full bg-navy/5 flex items-center justify-center font-display font-bold text-navy">
                  {u.name[0]}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-900">{u.name}</div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                </div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-gold">{u.role.replace("_", " ")}</div>
              </button>
            ))}
          </div>

          <div className="text-xs text-slate-400 mt-6 text-center">
            Demo accounts are pre-seeded with sample loads, bids, and notifications.
          </div>
        </div>
      </div>
    </div>
  );
}
